import { useEffect, useState, type ReactNode } from "react";
import type { User, Session } from "@supabase/supabase-js";
import { AuthContext } from "@/contexts/auth-context";
import { supabase } from "@/integrations/supabase/client";
import i18n, { getCurrentLanguage, normalizeLanguage } from "@/i18n/config";
import { markOnboardingForRegistration } from "@/lib/onboardingTour";
import { trackEvent } from "@/hooks/useAnalytics";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let initialized = false;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Ignore the initial null session emitted before storage is restored —
      // getSession() below is the source of truth for the first render.
      if (!initialized && event === 'INITIAL_SESSION' && !session) {
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);

      // Track OAuth completion (Google etc.) — fired on return from provider
      if (event === 'SIGNED_IN' && session?.user) {
        const pendingRaw = localStorage.getItem('vs_oauth_pending');
        if (pendingRaw) {
          try {
            const pending = JSON.parse(pendingRaw);
            const provider = (session.user.app_metadata?.provider as string) || pending.provider || 'oauth';
            const createdAt = session.user.created_at ? new Date(session.user.created_at).getTime() : 0;
            const isNewUser = createdAt > 0 && (Date.now() - createdAt) < 60_000;
            if (isNewUser) {
              markOnboardingForRegistration(session.user);
            }
            void trackEvent(
              isNewUser ? 'registration_complete' : 'login_complete',
              'conversion',
              {
                method: provider,
                funnel_step: isNewUser ? 'registration' : 'activation',
                oauth_started_at: pending.startedAt,
                oauth_duration_ms: Date.now() - (pending.startedAt || Date.now()),
              },
              session.user.id
            );
          } catch (e) {
            console.warn('OAuth tracking failed:', e);
          }
          localStorage.removeItem('vs_oauth_pending');
        }
      }
      // Don't set loading false here — wait for getSession
    });

    // Then restore session from storage. Never leave protected routes stuck on
    // a spinner if the auth endpoint briefly times out.
    const authTimeout = window.setTimeout(() => {
      if (!initialized) {
        initialized = true;
        setLoading(false);
      }
    }, 8000);

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
      })
      .catch(() => {
        setSession(null);
        setUser(null);
      })
      .finally(() => {
        window.clearTimeout(authTimeout);
        initialized = true;
        setLoading(false);
      });

    return () => {
      window.clearTimeout(authTimeout);
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    const syncProfileLanguage = (lng: string) => {
      const language = normalizeLanguage(lng);
      void supabase.from("profiles").update({ language }).eq("user_id", user.id);
    };
    syncProfileLanguage(getCurrentLanguage());
    i18n.on("languageChanged", syncProfileLanguage);
    return () => i18n.off("languageChanged", syncProfileLanguage);
  }, [user]);

  const signUp = async (email: string, password: string, fullName: string, phone?: string) => {
    const normalizedLang = normalizeLanguage(getCurrentLanguage());
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, language: normalizedLang },
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) throw error;

    return data.user ?? null;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
